// Web-exclusive Step: Job Details for Job Posting
// Phase 4: Final step of job posting wizard with Locum variant

import React from 'react';
import { useSelector } from 'react-redux';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, Switch } from 'react-native';
import { webColors, webBorderRadius } from '../../../constants/webStyles';
import JobListCard from '../components/JobListCard';
import WebDateTimePicker from '../../../components/web/WebDateTimePicker';
import WebExpirationDateSelect from '../../../components/web/WebExpirationDateSelect';
import WebCountrySelect from '../../../components/web/WebCountrySelect';
import WebMultiCountrySelect from '../../../components/web/WebMultiCountrySelect';
import { getCurrencySymbol } from '../../../utils/currency';
import { useIsMobile } from '../../../hooks/useIsMobile';

// Keep these in sync with the native Post Job screen (src/screens/Jobs/PostJob.js)
const fullTimePlaceholder = `Key responsibilities\n\nRequirements\n\nWhy join us\n\nRole summary\n\nAbout us\n\n`;
const locumPlaceholder = `Description: \nPlease describe incentives for procedures or treatments if any e.g.RTK / PCR / Injection additional allowance e.g.parking, meals, petrol if any.\nPreference of doctor e.g.female, good with kids, spoken language.`;

const StepJobDetails = ({ formData, updateFormData, clinicName }) => {
  // Local jobs are posted in the facility's own country (see PostJob.web.jsx)
  const { country } = useSelector((state) => state.user);
  const currencySymbol = getCurrencySymbol(country || 'MY');
  const isLocum = formData.jobType === 'locum';

  // Preview card info
  const getPreviewTitle = () => {
    if (formData.specialization) return formData.specialization;
    if (formData.professionSub) return formData.professionSub;
    return 'Job Title';
  };

  if (isLocum) {
    return <LocumJobForm formData={formData} updateFormData={updateFormData} clinicName={clinicName} />;
  }

  const isInternational = formData.jobType === 'international';
  if (isInternational) {
    return <InternationalJobForm formData={formData} updateFormData={updateFormData} clinicName={clinicName} isEditMode={formData._isEditMode} />;
  }

  const jobTypeMap = { locum: 0, fulltime: 1, temporary: 2, contract: 3, international: 4 };
  const previewJob = {
    title: getPreviewTitle(),
    subtitle: formData.subtitle,
    clinic_name: clinicName || 'Your Facility Name',
    type: jobTypeMap[formData.jobType] ?? 1,
    salary_min: formData.salary?.showSalary ? formData.salary?.min : null,
    salary_max: formData.salary?.showSalary ? formData.salary?.max : null,
    created_at: new Date().toISOString(),
    country: country || 'MY',
  };

  return (
    <View style={styles.container}>
      {/* Job Preview Card */}
      <JobListCard job={previewJob} isSelected={false} />

      {/* Subtitle */}
      <Text style={styles.sectionTitle}>Job title (be specific for better matches)</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="e.g. Staff Nurse – Night Shift, Penang"
        placeholderTextColor={webColors.textMuted}
        value={formData.subtitle}
        onChangeText={(value) => updateFormData('subtitle', value)}
      />

      {/* Job Description */}
      <Text style={styles.sectionTitle}>Job Description</Text>
      <TextInput
        style={styles.textarea}
        placeholder={fullTimePlaceholder}
        placeholderTextColor={webColors.textMuted}
        value={formData.description}
        onChangeText={(value) => updateFormData('description', value)}
        multiline
        numberOfLines={6}
        textAlignVertical="top"
      />

      {/* Person in-charge */}
      <Text style={styles.sectionTitle}>Person in-charge</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="Person in-charge to contact for job e.g. Nurse/manager"
        placeholderTextColor={webColors.textMuted}
        value={formData.contactPerson || ''}
        onChangeText={(value) => updateFormData('contactPerson', value)}
        maxLength={255}
      />

      {/* Post Expiration Date */}
      <Text style={styles.sectionTitle}>Post Expiration Date</Text>
      <Text style={styles.sectionDesc}>
        Select how long you would like this post to be visible for
      </Text>
      <WebExpirationDateSelect
        value={formData.expirationDate}
        onChange={(date) => updateFormData('expirationDate', date)}
      />
      <Text style={styles.helpText}>
        Job will be automatically hidden after this date
      </Text>

      {/* Salary Range */}
      <Text style={styles.sectionTitle}>Salary Range</Text>
      <View style={styles.salaryToggleRow}>
        <Text style={styles.salaryToggleLabel}>
          Display salary (by month) on this post
        </Text>
        <Switch
          value={formData.salary?.showSalary ?? true}
          onValueChange={(value) =>
            updateFormData('salary', { ...formData.salary, showSalary: value })
          }
          trackColor={{ false: webColors.border, true: webColors.primaryLight }}
          thumbColor={formData.salary?.showSalary ? webColors.primary : '#f4f3f4'}
        />
      </View>
      {formData.salary?.showSalary && (
        <View style={styles.salaryInputs}>
          <View style={styles.salaryInputWrapper}>
            <Text style={styles.currencyLabel}>{currencySymbol}</Text>
            <TextInput
              style={styles.salaryInput}
              placeholder="Min Salary"
              placeholderTextColor={webColors.textMuted}
              value={formData.salary?.min || ''}
              onChangeText={(value) =>
                updateFormData('salary', { ...formData.salary, min: value })
              }
              keyboardType="numeric"
            />
          </View>
          <Text style={styles.salaryDash}>-</Text>
          <View style={styles.salaryInputWrapper}>
            <Text style={styles.currencyLabel}>{currencySymbol}</Text>
            <TextInput
              style={styles.salaryInput}
              placeholder="Max Salary"
              placeholderTextColor={webColors.textMuted}
              value={formData.salary?.max || ''}
              onChangeText={(value) =>
                updateFormData('salary', { ...formData.salary, max: value })
              }
              keyboardType="numeric"
            />
          </View>
        </View>
      )}
    </View>
  );
};

// Locum-specific form
const LocumJobForm = ({ formData, updateFormData, clinicName }) => {
  const isMobile = useIsMobile();
  const { country } = useSelector((state) => state.user);
  const currencySymbol = getCurrencySymbol(country || 'MY');

  // Format Date to YYYY-MM-DD for API
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format Date to HH:MM for API
  const formatTimeForAPI = (date) => {
    if (!date) return null;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Parse date string to Date object
  const parseFormDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    return new Date(dateStr);
  };

  // Parse time string (HH:MM) to Date object
  const parseFormTime = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr instanceof Date) return timeStr;
    // Create a date object with just the time
    return new Date(`2000-01-01T${timeStr}:00`);
  };

  // Handle date/time changes
  const handleStartDateChange = (date) => {
    const formatted = formatDateForAPI(date);
    updateFormData('startDate', formatted);
    // Clear end date if it's now invalid
    if (formData.endDate && new Date(formData.endDate) < date) {
      updateFormData('endDate', null);
    }
  };

  const handleEndDateChange = (date) => {
    updateFormData('endDate', formatDateForAPI(date));
  };

  const handleStartTimeChange = (date) => {
    updateFormData('startTime', formatTimeForAPI(date));
  };

  const handleEndTimeChange = (date) => {
    updateFormData('endTime', formatTimeForAPI(date));
  };

  // Calculate minimum end date (must be >= start date)
  const minEndDate = formData.startDate ? new Date(formData.startDate) : undefined;

  // Validate end date/time is not before start date/time
  const getEndDateError = () => {
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      return 'End date cannot be before start date';
    }
    return null;
  };

  const getStartTimeError = () => {
    if (!formData.startDate || !formData.startTime) return null;
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}:00`).getTime();
    const oneHourFromNow = Date.now() + 60 * 60 * 1000;
    if (startDateTime < oneHourFromNow) {
      return 'Start time must be at least 1 hour from now';
    }
    return null;
  };

  const getEndTimeError = () => {
    const effectiveEndDate = formData.endDate || formData.startDate;
    if (formData.startDate && effectiveEndDate === formData.startDate && formData.startTime && formData.endTime) {
      if (formData.endTime <= formData.startTime) {
        return 'End time must be after start time';
      }
    }
    return null;
  };

  // Calculate total based on hours
  const calculateTotal = () => {
    const price = parseFloat(formData.pricePerHour) || 0;
    // Simple calculation - in real app would calculate from time difference
    const hours = calculateHours();
    return (price * hours).toFixed(0);
  };

  const calculateHours = () => {
    if (!formData.startTime || !formData.endTime) return 1;
    // Parse times (assuming HH:MM format)
    const start = parseTime(formData.startTime);
    const end = parseTime(formData.endTime);
    if (start === null || end === null) return 1;
    let diff = end - start;
    if (diff <= 0) diff += 24; // Handle overnight shifts
    return diff;
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d{1,2}):?(\d{2})?/);
    if (!match) return null;
    return parseInt(match[1]) + (parseInt(match[2] || '0') / 60);
  };

  const startTimestamp = formData.startDate && formData.startTime
    ? `${formData.startDate}T${formData.startTime}:00` : null;
  const effectiveEndDate = formData.endDate || formData.startDate;
  const endTimestamp = effectiveEndDate && formData.endTime
    ? `${effectiveEndDate}T${formData.endTime}:00` : null;

  const previewJob = {
    title: formData.specialization || formData.professionSub || 'Select a profession',
    subtitle: formData.subtitle,
    clinic_name: clinicName || 'Your Facility Name',
    type: 0,
    is_locum: true,
    rate_per_hour: formData.pricePerHour ? parseFloat(formData.pricePerHour) : null,
    price: formData.pricePerHour ? parseFloat(calculateTotal()) : null,
    start_timestamp: startTimestamp,
    end_timestamp: endTimestamp,
    created_at: new Date().toISOString(),
    country: country || 'MY',
  };

  return (
    <View style={styles.container}>
      {/* Locum Preview Card */}
      <JobListCard job={previewJob} isSelected={false} />

      {/* Date Pickers */}
      <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
        <View style={styles.formField}>
          <WebDateTimePicker
            label="Start Date"
            value={parseFormDate(formData.startDate)}
            onChange={handleStartDateChange}
            mode="date"
            placeholder="Select start date"
          />
        </View>
        <View style={styles.formField}>
          <WebDateTimePicker
            label="End Date"
            value={parseFormDate(formData.endDate)}
            onChange={handleEndDateChange}
            mode="date"
            placeholder="Select end date"
            minDate={minEndDate}
            error={getEndDateError()}
          />
        </View>
      </View>

      {/* Time Pickers */}
      <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
        <View style={styles.formField}>
          <WebDateTimePicker
            label="Start Time"
            value={parseFormTime(formData.startTime)}
            onChange={handleStartTimeChange}
            mode="time"
            placeholder="Select start time"
            error={getStartTimeError()}
          />
        </View>
        <View style={styles.formField}>
          <WebDateTimePicker
            label="End Time"
            value={parseFormTime(formData.endTime)}
            onChange={handleEndTimeChange}
            mode="time"
            placeholder="Select end time"
            error={getEndTimeError()}
          />
        </View>
      </View>

      {/* Price Calculation */}
      <Text style={styles.sectionTitle}>Payment</Text>
      <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
        <View style={styles.formField}>
          <Text style={styles.fieldLabel}>Price per hour</Text>
          <View style={styles.priceInputWrapper}>
            <Text style={styles.priceCurrency}>{currencySymbol}</Text>
            <TextInput
              style={styles.priceInput}
              placeholder="0"
              placeholderTextColor={webColors.textMuted}
              value={formData.pricePerHour || ''}
              onChangeText={(value) => updateFormData('pricePerHour', value)}
              keyboardType="numeric"
            />
            <Text style={styles.priceMultiplier}>
              x <Text style={styles.priceHours}>{calculateHours().toFixed(1)}</Text> hour =
            </Text>
          </View>
        </View>
        <View style={styles.formField}>
          <Text style={styles.fieldLabel}>Total</Text>
          <View style={styles.priceInputWrapper}>
            <Text style={styles.priceCurrency}>{currencySymbol}</Text>
            <TextInput
              style={[styles.priceInput, styles.priceInputDisabled]}
              placeholder="0"
              placeholderTextColor={webColors.textMuted}
              value={calculateTotal()}
              editable={false}
            />
          </View>
        </View>
      </View>

      {/* Subtitle */}
      <Text style={styles.sectionTitle}>Job title (be specific for better matches)</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="e.g. Staff Nurse – Night Shift, Penang"
        placeholderTextColor={webColors.textMuted}
        value={formData.subtitle || ''}
        onChangeText={(value) => updateFormData('subtitle', value)}
      />

      {/* Job Description */}
      <Text style={styles.sectionTitle}>Job Description</Text>
      <TextInput
        style={[styles.textarea, styles.textareaSmall]}
        placeholder={locumPlaceholder}
        placeholderTextColor={webColors.textMuted}
        value={formData.description || ''}
        onChangeText={(value) => updateFormData('description', value)}
        multiline
        numberOfLines={4}
        textAlignVertical="top"
      />

      {/* Person in-charge */}
      <Text style={styles.sectionTitle}>Person in-charge</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="Person in-charge to contact for job e.g. Nurse/manager"
        placeholderTextColor={webColors.textMuted}
        value={formData.contactPerson || ''}
        onChangeText={(value) => updateFormData('contactPerson', value)}
        maxLength={255}
      />
    </View>
  );
};

// International job form
const InternationalJobForm = ({ formData, updateFormData, clinicName, isEditMode }) => {
  const getPreviewTitle = () => {
    if (formData.specialization) return formData.specialization;
    if (formData.professionSub) return formData.professionSub;
    return 'Job Title';
  };

  const currencySymbol = getCurrencySymbol('USD');

  const previewJob = {
    title: getPreviewTitle(),
    subtitle: formData.subtitle,
    clinic_name: clinicName || 'Your Facility Name',
    type: 4,
    salary_min: formData.salary?.showSalary ? formData.salary?.min : null,
    salary_max: formData.salary?.showSalary ? formData.salary?.max : null,
    created_at: new Date().toISOString(),
    country: formData.targetCountry || 'US',
    location: formData.targetCountry || '',
  };

  return (
    <View style={styles.container}>
      {/* Job Preview Card */}
      <JobListCard job={previewJob} isSelected={false} />

      {/* Target Country - "In which country is this job located?" */}
      {isEditMode && formData.targetCountry ? (
        <View style={styles.readOnlySection}>
          <Text style={styles.sectionTitle}>Job Location</Text>
          <Text style={styles.readOnlyText}>
            This job is located in: {formData.targetCountry}
          </Text>
          <Text style={styles.readOnlyHint}>
            International status cannot be changed after posting
          </Text>
        </View>
      ) : (
        <View style={{ zIndex: 20 }}>
          <Text style={styles.sectionTitle}>In which country is this job located?</Text>
          <Text style={styles.sectionDesc}>
            Select the country where this position is based
          </Text>
          <WebCountrySelect
            value={formData.targetCountry}
            onChange={(code) => updateFormData('targetCountry', code)}
            placeholder="Select Target Country"
            error={formData._errors?.includes('targetCountry')}
          />
        </View>
      )}

      {/* Subtitle */}
      <Text style={styles.sectionTitle}>Job title (be specific for better matches)</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="e.g. Staff Nurse – Night Shift, Penang"
        placeholderTextColor={webColors.textMuted}
        value={formData.subtitle}
        onChangeText={(value) => updateFormData('subtitle', value)}
      />

      {/* Job Description */}
      <Text style={styles.sectionTitle}>Job Description</Text>
      <TextInput
        style={styles.textarea}
        placeholder={fullTimePlaceholder}
        placeholderTextColor={webColors.textMuted}
        value={formData.description}
        onChangeText={(value) => updateFormData('description', value)}
        multiline
        numberOfLines={6}
        textAlignVertical="top"
      />

      {/* Person in-charge */}
      <Text style={styles.sectionTitle}>Person in-charge</Text>
      <TextInput
        style={styles.subtitleInput}
        placeholder="Person in-charge to contact for job e.g. Nurse/manager"
        placeholderTextColor={webColors.textMuted}
        value={formData.contactPerson || ''}
        onChangeText={(value) => updateFormData('contactPerson', value)}
        maxLength={255}
      />

      {/* Post Expiration Date */}
      <Text style={styles.sectionTitle}>Post Expiration Date</Text>
      <Text style={styles.sectionDesc}>
        Select how long you would like this post to be visible for
      </Text>
      <WebExpirationDateSelect
        value={formData.expirationDate}
        onChange={(date) => updateFormData('expirationDate', date)}
      />
      <Text style={styles.helpText}>
        Job will be automatically hidden after this date
      </Text>

      {/* Salary Range (USD) */}
      <Text style={styles.sectionTitle}>Salary Range</Text>
      <View style={styles.salaryToggleRow}>
        <Text style={styles.salaryToggleLabel}>
          Display salary (by month) on this post
        </Text>
        <Switch
          value={formData.salary?.showSalary ?? true}
          onValueChange={(value) =>
            updateFormData('salary', { ...formData.salary, showSalary: value })
          }
          trackColor={{ false: webColors.border, true: webColors.primaryLight }}
          thumbColor={formData.salary?.showSalary ? webColors.primary : '#f4f3f4'}
        />
      </View>
      {formData.salary?.showSalary && (
        <View style={styles.salaryInputs}>
          <View style={styles.salaryInputWrapper}>
            <Text style={styles.currencyLabel}>{currencySymbol}</Text>
            <TextInput
              style={styles.salaryInput}
              placeholder="Min Salary"
              placeholderTextColor={webColors.textMuted}
              value={formData.salary?.min || ''}
              onChangeText={(value) =>
                updateFormData('salary', { ...formData.salary, min: value })
              }
              keyboardType="numeric"
            />
          </View>
          <Text style={styles.salaryDash}>-</Text>
          <View style={styles.salaryInputWrapper}>
            <Text style={styles.currencyLabel}>{currencySymbol}</Text>
            <TextInput
              style={styles.salaryInput}
              placeholder="Max Salary"
              placeholderTextColor={webColors.textMuted}
              value={formData.salary?.max || ''}
              onChangeText={(value) =>
                updateFormData('salary', { ...formData.salary, max: value })
              }
              keyboardType="numeric"
            />
          </View>
        </View>
      )}

      {/* Posting Countries - "Where would you like to post this job?" */}
      {isEditMode ? (
        <View style={styles.readOnlySection}>
          <Text style={styles.sectionTitle}>Posted In</Text>
          <Text style={styles.readOnlyText}>
            This job is posted in the selected countries
          </Text>
          <Text style={styles.readOnlyHint}>
            Posting countries cannot be changed after posting
          </Text>
        </View>
      ) : (
        <View style={{ zIndex: 10 }}>
          <Text style={styles.sectionTitle}>Where would you like to post this job?</Text>
          <Text style={styles.sectionDesc}>
            Select one or more countries where this job will be posted
          </Text>
          <WebMultiCountrySelect
            noUSD
            value={formData.selectedJobCountries || []}
            onChange={(codes) => updateFormData('selectedJobCountries', codes)}
            placeholder="Select Countries"
            error={formData._errors?.includes('countries')}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 6,
    marginTop: 8,
  },
  sectionDesc: {
    fontSize: 13,
    color: webColors.textMuted,
    marginBottom: 12,
    marginTop: -4,
  },
  subtitleInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    padding: 14,
    fontSize: 14,
    color: webColors.textPrimary,
  },
  textarea: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    padding: 14,
    fontSize: 14,
    color: webColors.textPrimary,
    minHeight: 150,
  },
  textareaSmall: {
    minHeight: 100,
  },
  helpText: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 8,
  },
  salaryToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  salaryToggleLabel: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  salaryInputs: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  salaryInputWrapper: {
    flex: 1,
    position: 'relative',
  },
  currencyLabel: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -7 }],
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textMuted,
    zIndex: 1,
  },
  salaryInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 46,
    paddingRight: 16,
    fontSize: 14,
    color: webColors.textPrimary,
  },
  salaryDash: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  formRow: {
    flexDirection: 'row',
    gap: 12,
  },
  formRowMobile: {
    flexDirection: 'column',
    gap: 0,
  },
  formField: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputIconLeft: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -8 }],
    fontSize: 16,
    zIndex: 1,
  },
  formInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 44,
    paddingRight: 16,
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  priceInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 14,
  },
  priceCurrency: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textMuted,
    marginRight: 8,
  },
  priceInput: {
    flex: 1,
    paddingVertical: 14,
    fontSize: 14,
    color: webColors.textPrimary,
  },
  priceInputDisabled: {
    backgroundColor: 'transparent',
    color: webColors.textSecondary,
  },
  priceMultiplier: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  priceHours: {
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  readOnlySection: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    padding: 16,
    marginTop: 8,
  },
  readOnlyText: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginTop: 4,
  },
  readOnlyHint: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 6,
    fontStyle: 'italic',
  },
});

export default StepJobDetails;
